import type { CSSProperties } from "react";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  formatAppointmentPurpose,
  purposeColorFromItem,
  purposeColorBackground,
} from "../../viewappointment/components/appointmentViewShared";

type OffCalendarVariant = "walkin" | "callin";

const VARIANT_UI: Record<
  OffCalendarVariant,
  {
    title: string;
    subtitle: string;
    Icon: typeof DirectionsWalkIcon;
    accent: string;
    accentSoft: string;
    accentRing: string;
    accentText: string;
    defaultColor: string;
  }
> = {
  walkin: {
    title: "Walk-in",
    subtitle: "Arrived without a booked slot",
    Icon: DirectionsWalkIcon,
    accent: "#0f766e",
    accentSoft: "bg-teal-50",
    accentRing: "ring-teal-200/80",
    accentText: "text-teal-800",
    defaultColor: "#0f766e",
  },
  callin: {
    title: "Call-in",
    subtitle: "Booked by phone, not on the grid",
    Icon: PhoneInTalkIcon,
    accent: "#0369a1",
    accentSoft: "bg-sky-50",
    accentRing: "ring-sky-200/80",
    accentText: "text-sky-800",
    defaultColor: "#0369a1",
  },
};

function initials(name?: string): string {
  const parts = String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ""}${parts[parts.length - 1][0] ?? ""}`.toUpperCase();
}

export default function WalkInAppointments({
  items,
  colorByPurpose,
  onSelect,
  variant = "walkin",
}: {
  items: any[];
  colorByPurpose?: boolean;
  onSelect: (appointment: any) => void;
  variant?: OffCalendarVariant;
}) {
  if (!items?.length) return null;

  const ui = VARIANT_UI[variant];
  const Icon = ui.Icon;

  return (
    <section
      className={`flex-1 min-w-0 overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-b from-white to-slate-50/80 shadow-[0_10px_40px_-24px_rgba(15,23,42,0.35)] ring-1 ${ui.accentRing}`}
    >
      <header className="relative flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3.5">
        <div
          className="pointer-events-none absolute inset-y-0 left-0 w-1"
          style={{ backgroundColor: ui.accent }}
          aria-hidden
        />
        <div className="flex items-start gap-3 min-w-0 pl-1">
          <div
            className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${ui.accentSoft}`}
            style={{ color: ui.accent }}
          >
            <Icon sx={{ fontSize: 22 }} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h5 className={`text-[15px] font-semibold tracking-tight ${ui.accentText}`}>
                {ui.title}
              </h5>
              <span
                className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-md px-1.5 text-[11px] font-semibold text-white"
                style={{ backgroundColor: ui.accent }}
              >
                {items.length}
              </span>
            </div>
            <p className="mt-0.5 text-xs text-slate-500">{ui.subtitle}</p>
          </div>
        </div>
      </header>

      <div className="p-3">
        <div className="grid grid-cols-1 gap-2.5 max-h-[260px] overflow-y-auto pr-0.5 sm:grid-cols-2">
          {items.map((event) => {
            const appointment = {
              ...event?.data?.appointment,
              _eventStart: event?.start,
              _eventEnd: event?.end,
            };
            const purpose = formatAppointmentPurpose(appointment);
            const purposeColor = purposeColorFromItem(appointment);
            const accentColor =
              appointment?.status === "completed"
                ? "#f9a8d4"
                : colorByPurpose && purposeColor
                  ? purposeColor
                  : appointment?.color || ui.defaultColor;
            const softBg =
              appointment?.status === "completed"
                ? "#fce7f3"
                : colorByPurpose && purposeColor
                  ? purposeColorBackground(purposeColor, 14)
                  : undefined;
            const timeLabel = [appointment?.startTime, appointment?.endTime]
              .filter(Boolean)
              .join(" – ");
            const key = String(
              appointment?.id ?? appointment?._id ?? event?._id ?? Math.random()
            );
            const name = appointment?.patientName || "Patient";

            return (
              <button
                key={key}
                type="button"
                onClick={() => onSelect(appointment)}
                className="group relative overflow-hidden rounded-xl border border-slate-200/90 bg-white text-left shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_12px_28px_-16px_rgba(15,23,42,0.35)] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={
                  {
                    backgroundColor: softBg || "#ffffff",
                  } as CSSProperties
                }
                title={[name, appointment?.dentist, purpose !== "N/A" ? purpose : null]
                  .filter(Boolean)
                  .join(" · ")}
              >
                <span
                  className="absolute inset-y-0 left-0 w-[3px] transition-all group-hover:w-1"
                  style={{ backgroundColor: accentColor }}
                  aria-hidden
                />

                <div className="flex gap-3 p-3.5 pl-4">
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold tracking-wide text-white shadow-sm"
                    style={{ backgroundColor: accentColor }}
                  >
                    {initials(name)}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-[13px] font-semibold text-slate-900">
                          {name}
                        </p>
                        {appointment?.regNo ? (
                          <p className="mt-0.5 truncate text-[11px] font-medium text-slate-400">
                            {appointment.regNo}
                          </p>
                        ) : null}
                      </div>
                      <ChevronRightIcon
                        sx={{ fontSize: 18 }}
                        className="mt-0.5 shrink-0 text-slate-300 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-slate-500"
                      />
                    </div>

                    <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                      {purpose && purpose !== "N/A" ? (
                        <span
                          className="inline-flex max-w-full items-center truncate rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white"
                          style={{ backgroundColor: accentColor }}
                        >
                          {purpose}
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                          {ui.title}
                        </span>
                      )}
                      {timeLabel ? (
                        <span className="inline-flex items-center rounded-md bg-white/80 px-2 py-0.5 text-[10px] font-medium text-slate-600 ring-1 ring-slate-200/80">
                          {timeLabel}
                        </span>
                      ) : null}
                    </div>

                    {appointment?.dentist ? (
                      <p className="mt-2 truncate text-[11px] text-slate-500">
                        <span className="font-medium text-slate-600">
                          {appointment.dentist}
                        </span>
                      </p>
                    ) : null}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
